import React, { useState, useEffect, useRef } from "react";
import {
    Sparkles,
    Mic,
    BookOpen,
    Volume2,
    ListOrdered,
    Speech,
    Globe,
    ChevronLeft,
    ChevronRight
} from "lucide-react";

const speakText = (text, selectedVoice) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.8;
    utter.pitch = 1.2;
    if (selectedVoice) utter.voice = selectedVoice;
    window.speechSynthesis.speak(utter);
};

const CVC_WORDS = [
    // --- Short 'A' Words ---
    { word: "CAT", sound: ["C", "A", "T"] },
    { word: "BAT", sound: ["B", "A", "T"] },
    { word: "MAT", sound: ["M", "A", "T"] },
    { word: "RAT", sound: ["R", "A", "T"] },
    { word: "PAN", sound: ["P", "A", "N"] },
    { word: "CAN", sound: ["C", "A", "N"] },
    { word: "FAN", sound: ["F", "A", "N"] },
    { word: "VAN", sound: ["V", "A", "N"] },
    { word: "CAP", sound: ["C", "A", "P"] },
    { word: "MAP", sound: ["M", "A", "P"] },
    { word: "TAP", sound: ["T", "A", "P"] },
    { word: "BAG", sound: ["B", "A", "G"] },

    // --- Short 'E' Words ---
    { word: "HEN", sound: ["H", "E", "N"] },
    { word: "PEN", sound: ["P", "E", "N"] },
    { word: "TEN", sound: ["T", "E", "N"] },
    { word: "NET", sound: ["N", "E", "T"] },
    { word: "JET", sound: ["J", "E", "T"] },
    { word: "WET", sound: ["W", "E", "T"] },
    { word: "BED", sound: ["B", "E", "D"] },
    { word: "RED", sound: ["R", "E", "D"] },
    { word: "LEG", sound: ["L", "E", "G"] },
    { word: "PEG", sound: ["P", "E", "G"] },

    // --- Short 'I' Words ---
    { word: "PIG", sound: ["P", "I", "G"] },
    { word: "WIG", sound: ["W", "I", "G"] },
    { word: "DIG", sound: ["D", "I", "G"] },
    { word: "BIN", sound: ["B", "I", "N"] },
    { word: "PIN", sound: ["P", "I", "N"] },
    { word: "WIN", sound: ["W", "I", "N"] },
    { word: "SIT", sound: ["S", "I", "T"] },
    { word: "HIT", sound: ["H", "I", "T"] },
    { word: "LIP", sound: ["L", "I", "P"] },
    { word: "ZIP", sound: ["Z", "I", "P"] },

    // --- Short 'O' Words ---
    { word: "DOG", sound: ["D", "O", "G"] },
    { word: "LOG", sound: ["L", "O", "G"] },
    { word: "JOG", sound: ["J", "O", "G"] },
    { word: "HOT", sound: ["H", "O", "T"] },
    { word: "POT", sound: ["P", "O", "T"] },
    { word: "COT", sound: ["C", "O", "T"] },
    { word: "FOX", sound: ["F", "O", "X"] },
    { word: "BOX", sound: ["B", "O", "X"] },
    { word: "MOP", sound: ["M", "O", "P"] },
    { word: "TOP", sound: ["T", "O", "P"] },

    // --- Short 'U' Words ---
    { word: "SUN", sound: ["S", "U", "N"] },
    { word: "RUN", sound: ["R", "U", "N"] },
    { word: "BUN", sound: ["B", "U", "N"] },
    { word: "MUG", sound: ["M", "U", "G"] },
    { word: "BUG", sound: ["B", "U", "G"] },
    { word: "JUG", sound: ["J", "U", "G"] },
    { word: "BUS", sound: ["B", "U", "S"] },
    { word: "CUP", sound: ["C", "U", "P"] },
    { word: "TUB", sound: ["T", "U", "B"] },
    { word: "NUT", sound: ["N", "U", "T"] },
    { word: "CUT", sound: ["C", "U", "T"] },
    { word: "GUM", sound: ["G", "U", "M"] }
];

const SIGHT_WORD_GROUPS = {
    "Level 1": ["the", "a", "is", "it", "in", "to", "and", "you", "am", "can", "go", "see", "we", "up", "look", "my"],
    "Level 2": ["he", "she", "they", "was", "for", "on", "are", "as", "with", "his", "at", "be", "this", "from", "have", "or"],
    "Level 3": ["one", "had", "by", "word", "but", "not", "what", "all", "were", "when", "your", "said", "there", "use", "an", "each"],
    "Level 4": ["which", "do", "how", "their", "if", "will", "up", "other", "about", "out", "many", "then", "them", "these", "so", "some"],
    "Level 5": ["her", "would", "make", "like", "him", "into", "time", "has", "look", "two", "more", "write", "go", "see", "number", "way"]
};

const STORIES = [
    // Animals & Nature (1-15)
    { title: "Max the Mouse", text: "Max the mouse had a red teapot. Inside he put three little seeds." },
    { title: "The Lost Kite", text: "Anna ran across the field chasing her bright yellow kite." },
    { title: "The Brave Turtle", text: "A small turtle tried climbing the tallest rock by the lake." },
    { title: "The Busy Bee", text: "A yellow bee flew over the garden to find a big pink flower." },
    { title: "Hungry Frog", text: "The green frog sat on a log. He saw a fly and went snap!" },
    { title: "Winter Bear", text: "The big brown bear found a cave. He will sleep until the sun comes back." },
    { title: "Fish Play", text: "Two little fish swim in the blue sea. They like to hide in the green grass." },
    { title: "Happy Bird", text: "A blue bird made a nest in the tree. She has three small blue eggs." },
    { title: "The Red Fox", text: "A red fox ran into the box. He wanted to hide from the big dog." },
    { title: "The Tall Giraffe", text: "The giraffe has a long neck. He can eat the green leaves on top of the tree." },
    { title: "Cat and Dog", text: "The cat sat on the mat. The dog came to play with a red ball." },
    { title: "Ant Hill", text: "Look at the tiny ant. It is carrying a big piece of an apple." },
    { title: "Funny Monkey", text: "The monkey likes to swing. He found a yellow banana in the tall tree." },
    { title: "The Pink Pig", text: "The pig played in the mud. Now the pig needs a big warm bath." },
    { title: "Owl at Night", text: "The wise owl stays up at night. He can see in the dark with his big eyes." },

    // Everyday Adventures (16-30)
    { title: "My New Bike", text: "I have a new blue bike. I wear my red helmet when I ride fast." },
    { title: "Baking Cookies", text: "Mom and I make cookies. We put sweet chocolate chips in the dough." },
    { title: "The Big Slide", text: "Tim went up the ladder. He slid down the big blue slide very fast." },
    { title: "Rainy Day", text: "The rain falls down. I put on my yellow coat and jump in the mud." },
    { title: "Lost Sock", text: "Where is my blue sock? I looked under the bed and found a toy car." },
    { title: "Bedtime Story", text: "Dad reads a book to me. I go to sleep with my soft brown teddy bear." },
    { title: "Apple Tree", text: "We went to the farm. I picked a big red apple from the tree." },
    { title: "School Bus", text: "The big yellow bus stops here. I see my friends and say hello." },
    { title: "Sand Castle", text: "At the beach I used my bucket. I made a tall castle in the sand." },
    { title: "Ice Cream", text: "The sun is hot today. I want a cold scoop of pink ice cream." },
    { title: "The Library", text: "The library is very quiet. I found a book about big space ships." },
    { title: "Painting Fun", text: "I have a brush and red paint. I am making a picture of a house." },
    { title: "Garden Water", text: "I use a green pot to water the seeds. Soon we will see a flower." },
    { title: "Lunch Time", text: "I have a sandwich and juice. My apple is red and very crunchy." },
    { title: "Hide and Seek", text: "I count to ten. My sister hides behind the big green chair." },

    // Fun & Fantasy (31-50)
    { title: "Space Trip", text: "The rocket ship went zoom. It flew past the moon and the stars." },
    { title: "The Magic Hat", text: "The bunny sat in the hat. When I said go, the bunny was gone!" },
    { title: "Robot Friend", text: "I have a toy robot. It can walk and talk with a funny beep." },
    { title: "Dragon Fire", text: "The green dragon was kind. He liked to eat red apples for lunch." },
    { title: "Star Wish", text: "I see a bright star in the sky. I make a wish before I go to sleep." },
    { title: "The Boat Trip", text: "The white boat sails on the lake. We saw a duck swim by us." },
    { title: "Big Balloon", text: "I have a red balloon. If I let go, it will fly up to the clouds." },
    { title: "The Toy Train", text: "The train goes choo-choo on the track. It carries blue blocks to the city." },
    { title: "Picnic Day", text: "We put bread and fruit in a basket. We sat on a blanket in the park." },
    { title: "The Drum", text: "I like to play my drum. Bang bang bang goes the loud music!" },
    { title: "Super Hero", text: "I put on my blue cape. I can run very fast like a super hero." },
    { title: "Gold Fish", text: "My fish lives in a bowl. He has orange scales and a tiny tail." },
    { title: "Funny Clown", text: "The clown had a red nose. He made a dog out of a long balloon." },
    { title: "The Pet Shop", text: "I saw a bird at the shop. It said hello to me and made me laugh." },
    { title: "Snow Man", text: "We made a man out of snow. We gave him a carrot for a nose." },
    { title: "A New Tooth", text: "My tooth fell out today. I will put it under my white pillow tonight." },
    { title: "The Farm Dog", text: "The dog barks at the sheep. He helps the man on the big farm." },
    { title: "Birthday Cake", text: "I am six years old today. My cake has six candles on top." },
    { title: "The Park Swing", text: "I like to go high on the swing. I can see over the tall wooden fence." },
    { title: "Small Bug", text: "A ladybug is red with black spots. It landed on my hand in the grass." }
];

const CvcBlendingActivity = ({ selectedVoice }) => {
    const [index, setIndex] = useState(0);
    const [step, setStep] = useState(0);
    const [filter, setFilter] = useState("All");
    
    // Filter logic
    const filteredWords = filter === "All" 
        ? CVC_WORDS 
        : CVC_WORDS.filter(w => w.sound[1] === filter);
    
    // Safely get the current word
    const word = filteredWords[index] || filteredWords[0];
    const totalWords = filteredWords.length;

    const speak = (t) => speakText(t, selectedVoice);

 const nextStep = () => {
    const ns = step + 1;
    setStep(ns);
    
    if (ns >= 1 && ns <= 3) {
        // Force lowercase so the TTS says "a" instead of "Capital A"
        speak(word.sound[ns - 1].toLowerCase()); 
    } else if (ns === 4) {
        speak(word.word);
    }
};

    const handleNextWord = () => {
        setIndex((prev) => (prev + 1) % totalWords);
        setStep(0);
    };

    const handlePrevWord = () => {
        setIndex((prev) => (prev - 1 + totalWords) % totalWords);
        setStep(0);
    };

    const handleFilterChange = (vowel) => {
        setFilter(vowel);
        setIndex(0);
        setStep(0);
    };

    return (
        <div className="space-y-6 p-2 sm:p-4">
            {/* 🎯 Filter Bar */}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
                {["All", "A", "E", "I", "O", "U"].map((v) => (
                    <button
                        key={v}
                        onClick={() => handleFilterChange(v)}
                        className={`px-3 py-1 rounded-full text-sm font-bold border-2 transition-all
                        ${filter === v 
                            ? "bg-pink-500 text-white border-pink-600 shadow-md" 
                            : "bg-white text-gray-600 border-gray-200 hover:border-pink-300"}`}
                    >
                        {v === "All" ? "All Words" : `Short ${v}`}
                    </button>
                ))}
            </div>

            {/* 📈 Progress Bar */}
            <div className="max-w-xs mx-auto space-y-1">
                <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    <span>Progress</span>
                    <span>{index + 1} / {totalWords}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-teal-500 transition-all duration-500" 
                        style={{ width: `${((index + 1) / totalWords) * 100}%` }}
                    />
                </div>
            </div>

            {/* 🔠 Responsive Letter Blocks */}
            <div className="flex justify-center space-x-2 sm:space-x-4 py-4">
                {word.sound.map((s, i) => (
                    <div
                        key={i}
                        className={`text-4xl sm:text-6xl font-black w-full max-w-[75px] sm:max-w-[100px] aspect-square flex items-center justify-center rounded-2xl border-b-4 sm:border-b-8 transition-all duration-300
                        ${step > i 
                            ? "bg-white text-pink-600 border-pink-200 shadow-xl scale-105" 
                            : "bg-gray-50 text-gray-300 border-gray-200"}`}
                    >
                        {s}
                    </div>
                ))}
            </div>

            {/* 📣 Word Display */}
            <div className="h-16 flex items-center justify-center">
                <h3 className={`text-4xl sm:text-5xl font-black tracking-widest transition-all duration-500 ${step === 4 ? "scale-110 text-teal-600" : "opacity-10 text-gray-300"}`}>
                    {word.word}
                </h3>
            </div>

            {/* 🎮 Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                {/* Prev Button */}
                <button
                    onClick={handlePrevWord}
                    className="hidden sm:flex items-center justify-center gap-2 font-bold text-gray-400 hover:text-pink-500 transition-colors"
                >
                    <ChevronLeft size={24} /> Previous
                </button>

                {/* Primary Action */}
<div className="flex flex-col gap-3 min-h-[140px]"> {/* Slightly taller for shadow room */}
    <button
        onClick={nextStep}
        // Button only disables AFTER the blending sound (step 4) is played
        disabled={step === 4}
        className={`w-full py-4 rounded-2xl text-xl font-black transition-all duration-300 border-b-0
            ${step === 4 
                ? "bg-gray-200 text-gray-400 opacity-50 cursor-not-allowed translate-y-[6px]" 
                : "bg-pink-600 text-white shadow-[0_6px_0_rgb(190,24,93)] active:shadow-none active:translate-y-[6px]"
            }`}
    >
        {/* Dynamic Text Logic */}
        {step < 3 ? (
            <span className="flex items-center justify-center gap-2">
                TAP SOUND <Volume2 size={20} />
            </span>
        ) : step === 3 ? (
            <span className="flex items-center justify-center gap-2">
                ⭐ BLEND WORD ⭐
            </span>
        ) : (
            "WELL DONE!"
        )}
    </button>
    
    {/* Next Word Button - Slides in only when Step 4 is reached */}
    <div className={`transition-all duration-500 transform ${
        step === 4 
            ? "opacity-100 translate-y-0 scale-100" 
            : "opacity-0 -translate-y-4 scale-95 pointer-events-none"
    }`}>
        <button
            onClick={handleNextWord}
            className="w-full py-3 bg-teal-500 text-white rounded-2xl font-extrabold shadow-[0_4px_0_rgb(13,148,136)] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2"
        >
            NEXT WORD <ChevronRight size={24} />
        </button>
    </div>
</div>

                {/* Speaker & Mobile Nav */}
                <div className="flex justify-center sm:justify-end gap-6">
                     <button
                        onClick={handlePrevWord}
                        className="sm:hidden p-4 bg-white border-2 border-gray-100 rounded-full text-gray-400"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={() => speak(word.word)}
                        className="p-4 bg-yellow-400 text-[#0A203F] rounded-full shadow-lg hover:rotate-12 transition-transform"
                    >
                        <Volume2 size={28} />
                    </button>
                    <button
                        onClick={handleNextWord}
                        className="sm:hidden p-4 bg-white border-2 border-gray-100 rounded-full text-gray-400"
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            </div>
        </div>
    );
};

const PhonicsActivityDisplay = ({ activity, selectedVoice }) => {
    const [levelIndex, setLevelIndex] = useState(0);
    const [currentWord, setCurrentWord] = useState(null);
    const [storyIndex, setStoryIndex] = useState(0);
    const [activeWordIndex, setActiveWordIndex] = useState(null);
    
    const levels = Object.entries(SIGHT_WORD_GROUPS);
    const words = STORIES[storyIndex].text.split(" ");

// Add this useEffect to your component to handle voice switches gracefully
useEffect(() => {
    window.speechSynthesis.cancel();
    setActiveWordIndex(null);
}, [selectedVoice, storyIndex]);

// Add these to your state/refs
const timerRef = useRef(null);

const readStory = () => {
    window.speechSynthesis.cancel();
    if (timerRef.current) clearInterval(timerRef.current);
    setActiveWordIndex(null);

    const text = STORIES[storyIndex].text;
    const wordsArray = text.split(/\s+/);
    const utterance = new SpeechSynthesisUtterance(text);
    
    utterance.voice = selectedVoice;
    // Lower rate makes it easier for the engine to fire events
    utterance.rate = 0.85; 

    // --- STRATEGY A: The Professional Way (onboundary) ---
    utterance.onboundary = (event) => {
        if (event.name === "word") {
            const textUntilNow = text.substring(0, event.charIndex + event.charLength);
            const currentIdx = textUntilNow.trim().split(/\s+/).length - 1;
            setActiveWordIndex(currentIdx);
            
            // If this fires, we know the voice supports boundaries, 
            // so we can kill any fallback timers.
            if (timerRef.current) clearInterval(timerRef.current);
        }
    };

    // --- STRATEGY B: The Fallback (If voice is "silent" on events) ---
    utterance.onstart = () => {
        // Estimate words per minute (approx 120-150 wpm for kids' rate)
        // We only use this if onboundary fails to trigger
        let estimatedIdx = 0;
        timerRef.current = setInterval(() => {
            if (activeWordIndex === null && estimatedIdx < wordsArray.length) {
                setActiveWordIndex(estimatedIdx);
                estimatedIdx++;
            }
        }, 450); // Approx 450ms per word at 0.85 rate
    };

    utterance.onend = () => {
        setActiveWordIndex(null);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    utterance.onerror = () => {
        setActiveWordIndex(null);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    window.speechSynthesis.speak(utterance);
};

    const handleListen = (word) => {
        setCurrentWord(word);
        speakText(word, selectedVoice);
    };

    if (!activity) return (
        <div className="text-center py-12 sm:py-20 bg-[#DDE7F6] rounded-3xl shadow-inner px-4">
            <Sparkles className="w-10 h-10 sm:w-12 text-pink-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#0F396E]">Choose an activity to begin!</h2>
        </div>
    );

    if (activity.key === "cvc") return <CvcBlendingActivity selectedVoice={selectedVoice} />;

    if (activity.key === "sight_words") return (
        <div className="p-2 sm:p-6 space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0F396E] flex justify-center items-center gap-3 text-center">
                <ListOrdered className="hidden sm:block" /> Practice Sight Words
            </h3>

            {currentWord && (
                <div className="text-center text-lg font-semibold text-[#0F396E] bg-blue-50 p-2 rounded-xl border border-blue-100 animate-pulse">
                    🔊 Listening: <span className="font-bold uppercase">{currentWord}</span>
                </div>
            )}

            <div className="space-y-6">
                <h4 className="text-xl font-bold text-[#0F396E] text-center bg-yellow-100 py-1 rounded-full w-32 mx-auto">
                    {levels[levelIndex][0]}
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-4 justify-center">
                    {levels[levelIndex][1].map((w) => (
                        <button
                            key={w}
                            onClick={() => handleListen(w)}
                            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold text-lg sm:text-xl transition-all border-b-4 
                            ${currentWord === w ? "bg-yellow-300 border-yellow-600 shadow-lg scale-105" : "bg-white border-gray-200 hover:border-[#0F396E] active:scale-95"}`}
                        >
                            {w}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-between sm:justify-center gap-4 mt-6">
                <button
                    onClick={() => setLevelIndex((prev) => Math.max(prev - 1, 0))}
                    disabled={levelIndex === 0}
                    className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-bold shadow transition ${levelIndex === 0 ? "bg-gray-200 text-gray-400" : "bg-[#0F396E] text-white"}`}
                >
                    <ChevronLeft className="inline mr-1" /> Prev
                </button>
                <button
                    onClick={() => setLevelIndex((prev) => Math.min(prev + 1, levels.length - 1))}
                    disabled={levelIndex === levels.length - 1}
                    className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-bold shadow transition ${levelIndex === levels.length - 1 ? "bg-gray-200 text-gray-400" : "bg-[#0F396E] text-white"}`}
                >
                    Next <ChevronRight className="inline ml-1" />
                </button>
            </div>
        </div>
    );

if (activity.key === "story_comp") return (
    <div className="p-3 sm:p-6 space-y-6 animate-in fade-in duration-500">
        {/* 🏆 Header & Progress Tracker */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 border-b border-gray-100 pb-4">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-[#0F396E]">
                    <Globe size={24} />
                </div>
                <div>
                    <h3 className="text-xl sm:text-2xl font-black text-[#0F396E] leading-none">Story Reading</h3>
                    {/* <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Level 1 Reader</p> */}
                </div>
            </div>
            
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full border border-gray-200">
                <span className="text-xs font-black text-gray-400 uppercase">Story {storyIndex + 1}/{STORIES.length}</span>
            </div>
        </div>

        {/* 📖 Story Card */}
        <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-100 to-teal-50 rounded-[2.5rem] blur-xl opacity-50"></div>
            <div className="relative bg-white border-2 border-indigo-50 p-6 sm:p-10 rounded-[2.5rem] shadow-sm min-h-[200px] flex flex-col justify-center">
                <h4 className="text-xl sm:text-2xl font-black text-center text-pink-500 mb-8 uppercase tracking-tight">
                    {STORIES[storyIndex].title}
                </h4>

                <div className="flex flex-wrap gap-x-2 gap-y-4 justify-center leading-relaxed">
                    {words.map((w, i) => (
                        <span 
                            key={i} 
                            onClick={() => speakText(w, selectedVoice)}
                            className={`
                                text-2xl sm:text-4xl font-bold px-2 py-1 rounded-2xl transition-all duration-150 cursor-pointer
                                ${activeWordIndex === i 
                                    ? "bg-yellow-300 text-blue-900 scale-125 shadow-lg rotate-2 z-20" 
                                    : "text-gray-600 hover:text-indigo-400"
                                }
                            `}
                        >
                            {w}
                        </span>
                    ))}
                </div>
            </div>
        </div>

        {/* ⚙️ Professional Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button 
                onClick={readStory} 
                className={`group relative flex items-center justify-center gap-3 py-5 rounded-3xl font-black text-xl shadow-[0_6px_0_rgb(5,25,55)] active:shadow-none active:translate-y-[6px] transition-all
                    ${activeWordIndex !== null ? "bg-indigo-400 opacity-90" : "bg-[#0F396E] text-white"}`}
            >
                <div className={`p-2 bg-white/10 rounded-full ${activeWordIndex !== null ? "animate-pulse" : ""}`}>
                    <Volume2 size={24} />
                </div>
                {activeWordIndex !== null ? "READING..." : "READ ALOUD"}
            </button>

            <div className="flex gap-3">
                <button
                    onClick={() => { setStoryIndex(p => Math.max(p - 1, 0)); setActiveWordIndex(null); }}
                    disabled={storyIndex === 0}
                    className="flex-1 bg-white border-2 border-gray-200 text-gray-600 px-4 py-3 rounded-2xl font-bold hover:border-teal-500 hover:text-teal-600 disabled:opacity-30 transition-all flex items-center justify-center gap-2"
                >
                    <ChevronLeft size={20} /> PREV
                </button>
                <button
                    onClick={() => { setStoryIndex(p => Math.min(p + 1, STORIES.length - 1)); setActiveWordIndex(null); }}
                    disabled={storyIndex === STORIES.length - 1}
                    className="flex-1 bg-teal-500 text-white px-4 py-3 rounded-2xl font-bold shadow-[0_4px_0_rgb(13,148,136)] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2"
                >
                    NEXT <ChevronRight size={20} />
                </button>
            </div>
        </div>

        <p className="text-center text-gray-400 text-xs font-bold uppercase tracking-tighter">
            Tip: Tap a word to hear it by itself!
        </p>
    </div>
);

    return <div className="p-10 text-center">Activity not found</div>;
};

const ReadingAndPhonicsPage = () => {
    const [activity, setActivity] = useState(null);
    const [voices, setVoices] = useState([]);
    const [preferredVoice, setPreferredVoice] = useState(null);
    const voiceListRef = useRef([]);

    const activities = [
        { key: "cvc", title: "CVC Game", icon: <Speech />, color: "bg-pink-600" },
        { key: "sight_words", title: "Sight Words", icon: <ListOrdered />, color: "bg-teal-600" },
        { key: "story_comp", title: "Stories", icon: <Globe />, color: "bg-[#0F396E]" },
    ];

    useEffect(() => {
        if (!("speechSynthesis" in window)) return;
        const load = () => {
            const list = window.speechSynthesis.getVoices();
            if (list.length && !voiceListRef.current.length) {
                voiceListRef.current = list;
                setVoices(list);
                setPreferredVoice(list.find((v) => v.lang.startsWith("en-")) || list[0]);
            }
        };
        window.speechSynthesis.onvoiceschanged = load;
        load();
    }, []);

    return (
        <div className="min-h-screen bg-[#F0F5FE] pb-10">
            {/* Header Area */}
            <div className="bg-[#0F396E] text-white p-4 sm:p-6 shadow-lg mb-6">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
                    <h1 className="text-xl sm:text-2xl font-black flex items-center gap-2">
                        <BookOpen className="text-yellow-400" /> PHONICS ADVENTURE
                    </h1>

                    <div className="flex items-center gap-2 bg-white/10 p-2 rounded-lg border border-white/20 w-full sm:w-auto">
                        <Mic size={18} className="text-yellow-400 shrink-0" />
                        <select
                            className="bg-transparent text-white text-sm focus:outline-none w-full"
                            value={preferredVoice?.name || ""}
                            onChange={(e) => setPreferredVoice(voices.find((v) => v.name === e.target.value))}
                        >
                            {voices.map((v) => (
                                <option key={v.name} value={v.name} className="text-black">
                                    {v.name.substring(0, 20)}...
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Selection Tabs - Fully Responsive Scroll/Wrap */}
            <div className="max-w-4xl mx-auto px-4 mb-8">
                <div className="flex flex-wrap sm:flex-nowrap justify-center gap-2 sm:gap-4 bg-white p-2 rounded-2xl shadow-md border border-gray-100">
                    {activities.map((a) => (
                        <button
                            key={a.key}
                            onClick={() => setActivity(a)}
                            className={`flex-1 min-w-[120px] px-4 py-3 rounded-xl text-sm sm:text-base font-bold flex items-center justify-center gap-2 transition-all
                            ${activity?.key === a.key ? `${a.color} text-white shadow-lg scale-105` : "text-gray-600 hover:bg-gray-50"}`}
                        >
                            {React.cloneElement(a.icon, { size: 18 })}
                            {a.title}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <main className="max-w-4xl mx-auto px-4">
                <div className="bg-white rounded-[2rem] shadow-2xl border-t-8 border-pink-500 overflow-hidden min-h-[400px]">
                    <PhonicsActivityDisplay activity={activity} selectedVoice={preferredVoice} />
                </div>
                
                <p className="text-center text-gray-400 text-sm mt-8">
                    Tip: Use headphones for the best Phonics experience! 🎧
                </p>
            </main>
        </div>
    );
};

export default ReadingAndPhonicsPage;